import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Image as ImageIcon } from 'lucide-react';
import { Thumbnail } from './LightboxProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAT_ORDER = ['before', 'after', 'asset', 'data_plate', 'installation', 'diagnostic', 'damage', 'treatment_area', 'property_area', 'inspection', 'other'];
const CAT_LABEL = {
  before: 'Before', after: 'After', asset: 'Asset', data_plate: 'Data Plate',
  installation: 'Installation', diagnostic: 'Diagnostic', damage: 'Damage',
  treatment_area: 'Treatment Area', property_area: 'Property Area', inspection: 'Inspection', other: 'Other',
};

/**
 * Job Photos / Documentation — reads canonical job_media by any relationship
 * (appointment/completion/invoice/work order). One binary, many relationships:
 * no duplicate storage. Renders nothing when there are no photos.
 */
const JobMediaGallery = ({ appointmentId, completionId, invoiceId, workOrderNumber, title = 'Job Photos', testId = 'job-media-gallery' }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const params = {};
      if (appointmentId) params.appointment_id = appointmentId;
      if (completionId) params.completion_id = completionId;
      if (invoiceId) params.invoice_id = invoiceId;
      if (workOrderNumber) params.work_order_number = workOrderNumber;
      if (Object.keys(params).length === 0) { setLoading(false); return; }
      try {
        const { data } = await axios.get(`${API}/job-media`, { withCredentials: true, params });
        if (alive) setMedia(data || []);
      } catch { if (alive) setMedia([]); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [appointmentId, completionId, invoiceId, workOrderNumber]);

  if (loading || media.length === 0) return null;
  const byCat = {};
  media.forEach((m) => { (byCat[m.category] = byCat[m.category] || []).push(m); });
  const allUrls = media.map((m) => `${BACKEND_URL}${m.url}`);

  return (
    <div data-testid={testId}>
      <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider mb-2 flex items-center gap-1">
        <ImageIcon className="w-3.5 h-3.5" /> {title} ({media.length})
      </p>
      <div className="space-y-3">
        {CAT_ORDER.filter((c) => byCat[c]).map((cat) => (
          <div key={cat}>
            <p className="text-[11px] text-[#737373] mb-1">{CAT_LABEL[cat] || cat}</p>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {byCat[cat].map((m) => {
                const url = `${BACKEND_URL}${m.url}`;
                return (
                  <div key={m.id} className="bg-[#0A0A0A] border border-[#262626] rounded overflow-hidden" data-testid={`job-media-${cat}`}>
                    <Thumbnail src={url} images={allUrls} index={allUrls.indexOf(url)} alt={cat} className="w-full h-16 object-cover" />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default JobMediaGallery;
